import {
    faBowlFood,
    faDroplet,
    faFaceGrin,
    faFaceHeadBandage,
    faImage,
    faKitMedical,
    faSnooze,
    faStickyNote,
    faToilet,
    faVideo
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActivityType, HydrationIntake, ReliefCategory, RestValue } from '@spike/activity-model';
import styles from 'components/Activities/Icon/ActivityIcon.module.scss';
import { SelectDropdownOption } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import React from 'react';

interface ActivityIconProps {
    size?: number;
    type: ActivityType;
}

export const ALL_ACTIVITY_TYPES: Array<ActivityType> = [
    ActivityType.PHOTO,
    ActivityType.VIDEO,
    ActivityType.FEEDING,
    ActivityType.MEDICATION,
    ActivityType.HYDRATION,
    ActivityType.RELIEFS,
    ActivityType.REST,
    ActivityType.INCIDENT,
    ActivityType.ENRICHMENT,
    ActivityType.NOTE
];

export const ACTIVITY_LABEL_MAP: Record<ActivityType, string> = {
    [ActivityType.PHOTO]: 'Photo',
    [ActivityType.VIDEO]: 'Video',
    [ActivityType.FEEDING]: 'Feeding',
    [ActivityType.MEDICATION]: 'Medication',
    [ActivityType.NOTE]: 'Note',
    [ActivityType.INCIDENT]: 'Incident',
    [ActivityType.ENRICHMENT]: 'Enrichment',
    [ActivityType.HYDRATION]: 'Hydration',
    [ActivityType.RELIEFS]: 'Relief',
    [ActivityType.REST]: 'Rest'
};

export const ACTIVITY_TITLE_MAP: Record<ActivityType, string> = {
    [ActivityType.PHOTO]: 'Photo',
    [ActivityType.VIDEO]: 'Video',
    [ActivityType.FEEDING]: 'Feeding',
    [ActivityType.MEDICATION]: 'Medication',
    [ActivityType.NOTE]: 'Note',
    [ActivityType.INCIDENT]: 'Incident',
    [ActivityType.ENRICHMENT]: 'Enrichment',
    [ActivityType.HYDRATION]: 'Hydration',
    [ActivityType.RELIEFS]: 'Relief',
    [ActivityType.REST]: 'Rest'
};

export const HYDRATION_LABELS: Record<HydrationIntake, string> = {
    [HydrationIntake.ACCEPTED_ALL]: 'Accepted All',
    [HydrationIntake.PARTIAL]: 'Partial',
    [HydrationIntake.NONE]: 'None'
};

export const REST_LABELS: Record<RestValue, string> = {
    [RestValue.YES]: 'Yes',
    [RestValue.NO]: 'No',
    [RestValue.PARTIAL]: 'Partial'
};

export const RELIEF_CATEGORY_LABELS: Record<ReliefCategory, string> = {
    [ReliefCategory.PEED]: 'Peed',
    [ReliefCategory.POOPED]: 'Pooped'
};

export const RELIEF_SLOTS: Array<{ key: 'morning' | 'afternoon' | 'evening'; label: string }> = [
    { key: 'morning', label: 'Morning' },
    { key: 'afternoon', label: 'Afternoon' },
    { key: 'evening', label: 'Evening' }
];

export const ACTIVITY_FORM_CARD_COLORS_MAP: Record<ActivityType, string> = {
    [ActivityType.PHOTO]: 'var(--color-activity-photo)',
    [ActivityType.VIDEO]: 'var(--color-activity-video)',
    [ActivityType.FEEDING]: 'var(--color-activity-feeding)',
    [ActivityType.MEDICATION]: 'var(--color-activity-medication)',
    [ActivityType.NOTE]: 'var(--color-activity-note)',
    [ActivityType.INCIDENT]: 'var(--color-activity-incident)',
    [ActivityType.ENRICHMENT]: 'var(--color-activity-enrichment)',
    [ActivityType.HYDRATION]: 'var(--color-activity-hydration)',
    [ActivityType.RELIEFS]: 'var(--color-activity-reliefs)',
    [ActivityType.REST]: 'var(--color-activity-rest)'
};

export const ACTIVITY_ICON_MAP: Record<ActivityType, IconDefinition> = {
    [ActivityType.PHOTO]: faImage,
    [ActivityType.VIDEO]: faVideo,
    [ActivityType.FEEDING]: faBowlFood,
    [ActivityType.MEDICATION]: faKitMedical,
    [ActivityType.NOTE]: faStickyNote,
    [ActivityType.INCIDENT]: faFaceHeadBandage,
    [ActivityType.ENRICHMENT]: faFaceGrin,
    [ActivityType.HYDRATION]: faDroplet,
    [ActivityType.RELIEFS]: faToilet,
    [ActivityType.REST]: faSnooze
};

export const ACTIVITY_ICON_COLORS_MAP: Record<ActivityType, string> = {
    [ActivityType.PHOTO]: 'var(--color-activity-photo)',
    [ActivityType.VIDEO]: 'var(--color-activity-video)',
    [ActivityType.FEEDING]: 'var(--color-activity-feeding)',
    [ActivityType.MEDICATION]: 'var(--color-activity-medication)',
    [ActivityType.NOTE]: 'var(--color-activity-note)',
    [ActivityType.INCIDENT]: 'var(--color-activity-incident)',
    [ActivityType.ENRICHMENT]: 'var(--color-activity-enrichment)',
    [ActivityType.HYDRATION]: 'var(--color-activity-hydration)',
    [ActivityType.RELIEFS]: 'var(--color-activity-reliefs)',
    [ActivityType.REST]: 'var(--color-activity-rest)'
};

export const ACTIVITY_ICON_FOREGROUND_COLORS_MAP: Record<ActivityType, string> = {
    [ActivityType.PHOTO]: 'var(--color-activity-photo-foreground)',
    [ActivityType.VIDEO]: 'var(--color-activity-video-foreground)',
    [ActivityType.FEEDING]: 'var(--color-activity-feeding-foreground)',
    [ActivityType.MEDICATION]: 'var(--color-activity-medication-foreground)',
    [ActivityType.NOTE]: 'var(--color-activity-note-foreground)',
    [ActivityType.INCIDENT]: 'var(--color-activity-incident-foreground)',
    [ActivityType.ENRICHMENT]: 'var(--color-activity-enrichment-foreground)',
    [ActivityType.HYDRATION]: 'var(--color-activity-hydration-foreground)',
    [ActivityType.RELIEFS]: 'var(--color-activity-reliefs-foreground)',
    [ActivityType.REST]: 'var(--color-activity-rest-foreground)'
};

export const ACTIVITY_FILTER_OPTIONS: Array<SelectDropdownOption<ActivityType>> = ALL_ACTIVITY_TYPES.map(type => ({
    id: type,
    label: ACTIVITY_LABEL_MAP[type]
}));

export const ACTIVITY_CREATE_OPTIONS: Array<{
    id: ActivityType;
    label: string;
    icon: IconDefinition;
}> = ALL_ACTIVITY_TYPES.map(type => ({
    id: type,
    label: ACTIVITY_LABEL_MAP[type],
    icon: ACTIVITY_ICON_MAP[type]
}));

export const ActivityIcon: React.FC<ActivityIconProps> = ({ size = 46, type }) => {
    return (
        <div
            className={styles.root}
            style={
                {
                    '--size': `${size}px`,
                    'color': ACTIVITY_ICON_FOREGROUND_COLORS_MAP[type],
                    'backgroundColor': ACTIVITY_ICON_COLORS_MAP[type]
                } as React.CSSProperties
            }
        >
            <FontAwesomeIcon icon={ACTIVITY_ICON_MAP[type]} />
        </div>
    );
};
